import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format, parseISO } from "date-fns";
import ExcelJS from "exceljs";

import { Button, Dropdown, Row } from "@deere/ux.uxframe-react";
import {
  expirationReportList,
  expSearchQueryObject,
  setExpirationToDefault,
} from "./expirationReportSlice";
import {
  TextFilter,
  WtyReportTable,
} from "../common/wty-report-table/WtyReportTable";
import { WtyReportMachineListPagination } from "../machine-search/MachineList";
import wtyReportApi from "../../services/warrantyReportAxios";
import { printContentById } from "../common/PrintUtils";
import { languageResponseData } from "../Translation/translationSerachSlice";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

export const ExpirationReportList = ({ expirationSearchPayload }) => {
  const translationsResponse = useSelector(languageResponseData);
  const dispatch = useDispatch();
  const history = useHistory();
  const translate = (key, defaultText) => {
    return translationsResponse[key] || defaultText;
  };

  const machineList = useSelector(expirationReportList);
  const expReportListHeaders = [
    {
      Header: translate("pin", "Pin"),
      accessor: "pin",
      Filter: TextFilter,
    },
    {
      Header: translate("model", "Model"),
      accessor: "model",
      Filter: TextFilter,
    },
    {
      Header: translate("description", "Description"),
      accessor: "modelDescription",
      Filter: TextFilter,
    },
    {
      Header: translate("delivery_date", "Delivery Date"),
      accessor: "deliveryDate",
      Filter: TextFilter,
    },
    {
      Header: translate("responsible_dealer", "Responsible Dealer"),
      accessor: "responsibleDealer",
      Filter: TextFilter,
    },
    {
      Header: translate("warranty_option", "Warranty Option"),
      accessor: "warrantyTypeName",
      Filter: TextFilter,
    },
    {
      Header: translate("expiration_date", "Expiration Date"),
      accessor: "warrantyEndDate",
      Filter: TextFilter,
    },
    {
      Header: translate("expires_in_days", "Expires in days"),
      accessor: "daysRemaining",
      Filter: TextFilter,
    },
  ];

  let machineListTableData = [];

  /*Mapping machine data to table object*/

  if (machineList && Array.isArray(machineList) && machineList.length > 0) {
    machineListTableData = machineList.map((machineData) => {
      const indCustomerName = [
        machineData.firstName,
        machineData.middleName,
        machineData.lastName,
      ]
        .map((name) => name?.trim())
        .filter(Boolean)
        .join(" ");
      const customerName =
        machineData.businessName1?.trim() ||
        machineData.businessName2?.trim() ||
        indCustomerName ||
        "N/A";
      return {
        pin: machineData.pin,
        model: machineData.model,
        modelDescription: machineData.modelDescription,
        modelYear: machineData.modelYear,
        deliveryDate: machineData.deliveryDate
          ? format(parseISO(machineData.deliveryDate), "yyyy-MM-dd")
          : "",
        responsibleDealer: machineData.serviceDealerNumber,
        warrantyTypeName: machineData.warrantyTypeName,
        warrantyEndDate: machineData.warrantyEndDate
          ? format(parseISO(machineData.warrantyEndDate), "yyyy-MM-dd")
          : "",
        daysRemaining: machineData.daysRemaining,
      };
    });
  }

  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      if (!location.pathname.startsWith("/machine-details")) {
        dispatch(setExpirationToDefault());
      }
    });

    return () => {
      unlisten();
    };
  }, [dispatch, history]);

  return (
    <div>
      <Row
        style={{
          marginBottom: ".5rem",
          marginTop: "1rem",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Button
          variant="secondary"
          onClick={() =>
            printContentById("expiration-report-data", "Expiration Report")
          }
        >
          Print to Pdf
        </Button>{" "}
        &nbsp; &nbsp;
        <ExportMachineListExpirationReport
          machines={machineList}
          searchType={"expiration-report"}
        />
      </Row>
      <WtyReportMachineListPagination
        searchType={"expiration-report"}
        expirationSearchPayload={expirationSearchPayload}
      />
      <div id="expiration-report-data">
        <WtyReportTable
          columns={expReportListHeaders}
          data={machineListTableData}
          dataType={"MachineData"}
          searchFrom={"expiration-report"}
          machineSearchType={"expiration-report"}
        />
      </div>
      <WtyReportMachineListPagination searchType={"expiration-report"} />
      <Row
        style={{
          marginBottom: ".5rem",
          marginTop: "1rem",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Button
          variant="secondary"
          onClick={() =>
            printContentById("expiration-report-data", "Expiration Report")
          }
        >
          Print to Pdf
        </Button>{" "}
        &nbsp; &nbsp;
        <ExportMachineListExpirationReport
          machines={machineList}
          searchType={"expiration-report"}
        />
      </Row>
    </div>
  );
};

const ExportMachineListExpirationReport = ({ machines }) => {
  /*Since the expiration report is run for a dealer or list of dealers we can get dealer from any machine data*/

  let machineListHeader = [],
    machineListData = [];

  const expirationSearchQueryObj = useSelector(expSearchQueryObject);
  const getAllExpirationDataForExport = async () => {
    try {
      const response = await wtyReportApi.post(
        "/machine-search/expiration-report-export",
        expirationSearchQueryObj
      );
      if (response.status !== 200) {
        new Error("Calling api to get Expiration report failed.");
      }
      return await response.data?.machines;
    } catch (error) {
      throw error;
    }
  };
  const handleExportMachineList = async () => {
    prepareMachineDataForExport(machines);

    const workbook = new ExcelJS.Workbook();
    const machineSearchSheet = workbook.addWorksheet("Expiration Report");
    let fileName = `ExpirationReport-${machines[0]?.serviceDealerNumber}.xlsx`;
    machineListHeader.forEach((row) => {
      const cell = machineSearchSheet.addRow(row);
      cell.eachCell({ includeEmpty: true }, (cell, cellNum) => {
        cell.style.font = { bold: true };
        cell.style.alignment = { horizontal: "left" };
      });
    });
    machineListData.forEach((row, rowNumber) => {
      const cell = machineSearchSheet.addRow(row);
    });

    const blob = await workbook.xlsx.writeBuffer();

    const blobURL = window.URL.createObjectURL(new Blob([blob]));
    const a = document.createElement("a");
    a.href = blobURL;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(blobURL);
  };

  const handleExportMachineListAll = async () => {
    const machinesAll = await getAllExpirationDataForExport();

    prepareMachineDataForExport(machinesAll);

    /*Update file name*/
    let fileName = `ExpirationReport_All_${machinesAll[0]?.serviceDealerNumber}.xlsx`;

    const workbook = new ExcelJS.Workbook();
    const machineSearchSheet = workbook.addWorksheet("Expiration Report");

    machineListHeader.forEach((row) => {
      const cell = machineSearchSheet.addRow(row);
      cell.eachCell({ includeEmpty: true }, (cell, cellNum) => {
        cell.style.font = { bold: true };
        cell.style.alignment = { horizontal: "left" };
      });
    });
    machineListData.forEach((row, rowNumber) => {
      const cell = machineSearchSheet.addRow(row);
    });

    const blob = await workbook.xlsx.writeBuffer();

    const blobURL = window.URL.createObjectURL(new Blob([blob]));
    const a = document.createElement("a");
    a.href = blobURL;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(blobURL);
  };

  const prepareMachineDataForExport = (machines) => {
    machineListHeader = [
      [
        "PIN",
        "Model",
        "Description",
        "Delivery Date",
        "Responsible Dealer",
        "Warranty Option",
        "Exp. date",
        "Expires in Days",
      ],
    ];

    const getFormattedDateForExport = (inputDate) => {
      const dateWithoutOffset = inputDate?.slice(0, -6);
      const dateObj = parseISO(dateWithoutOffset);
      if (!isNaN(dateObj)) {
        return format(dateObj, "dd MMM yyyy");
      } else {
        return "";
      }
    };
    machineListData = machines.map(
      ({
        pin,
        model,
        modelDescription,
        deliveryDate,
        serviceDealerNumber,
        warrantyTypeName,
        warrantyEndDate,
        daysRemaining,
      }) => {
        const formattedDeliveryDate = getFormattedDateForExport(deliveryDate);
        const formattedWarrantyEndDate =
          getFormattedDateForExport(warrantyEndDate);
        // TODO: Commented the data and header for the Open PIP column
        return [
          pin,
          model,
          modelDescription,
          formattedDeliveryDate,
          serviceDealerNumber,
          warrantyTypeName,
          formattedWarrantyEndDate,
          daysRemaining,
        ];
      }
    );
  };

  return (
    <Dropdown>
      <Dropdown.Toggle variant="secondary" id="dropdown-basic">
        {"Export Data"}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item href="#" onClick={handleExportMachineList}>
          {"Export To Excel"}
        </Dropdown.Item>
        <Dropdown.Item href="#" onClick={handleExportMachineListAll}>
          {"Export All"}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
    /*<Button variant={"secondary"} onClick={handleExportMachineList}>
            Export To Excel
        </Button>*/
  );
};
